import React from 'react';

import {
	IProductTemplateConfig,
	IProductTemplateData,
} from 'components/dashboard/product-template-table/utils/interfaces';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputText } from 'primereact/inputtext';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import {
	CellType,
	ProductTemplateSortingStrategy,
	ProductTemplateSortingStrategyOptionsOrder,
	TemplateLoadingType,
} from 'utils/enums';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Fieldset } from 'primereact/fieldset';
import { Button } from 'primereact/button';
import { Tooltip } from 'react-tooltip';
import InputLabelWrapper from 'components/basic/input-label-wrapper/InputLabelWrapper';
import { ChronoUnitLegacy } from 'utils/time-utils';
import { T } from "services/lang/LanguageService";

type ProductTemplateSettingsConfigurationProps = {
	data: IProductTemplateData;
	onSettingsChange: (newSettings: IProductTemplateConfig) => void;
};

const ProductTemplateSettingsConfiguration: React.FC<
	ProductTemplateSettingsConfigurationProps
> = ({ data, onSettingsChange }) => {
	const { config, attributes } = data;
	const settings = config ?? {};
	const attributesCopy = [...attributes];

	const onFieldChange = (
		e: React.ChangeEvent<HTMLInputElement> | DropdownChangeParams,
	) => {
		onSettingsChange({
			...settings,
			[e.target.name]: e.target.value,
		});
	};

	const onLoadingConfigChange = (
		e: React.ChangeEvent<HTMLInputElement> | DropdownChangeParams,
	) => {
		onSettingsChange({
			...settings,
			loadingConfig: {
				...settings.loadingConfig,
				[e.target.name]: e.target.value,
			},
		});
	};

	const onSortingStrategyOptionChange = (
		e: React.ChangeEvent<HTMLInputElement> | DropdownChangeParams,
		index: number,
	) => {
		const columns = [...(settings.sortingStrategyOptions?.columns ?? [])];
		columns[index] = {
			...columns[index],
			[e.target.name]: e.target.value,
		};

		onSettingsChange({
			...settings,
			sortingStrategyOptions: {
				columns,
			},
		});
	};

	const addSortingStrategyOption = () => {
		onSettingsChange({
			...settings,
			sortingStrategyOptions: {
				columns: [
					...(settings.sortingStrategyOptions?.columns ?? []),
					{
						column: '',
					},
				],
			},
		});
	};

	const removeSortingStrategyOption = (index: number) => {
		const columns = [...(settings.sortingStrategyOptions?.columns ?? [])];
		columns.splice(index, 1);

		onSettingsChange({
			...settings,
			sortingStrategyOptions: {
				columns,
			},
		});
	};

	const mappedSortingStrategyOptions =
		settings.sortingStrategyOptions?.columns?.map((col, index) => (
			<InputLabelWrapper labelName={`${index + 1} - ${T("word.sorting-attribute")}`} key={col.column}>
				<div className='p-inputgroup'>
					<Dropdown
						options={attributesCopy.map(att => att.referenceName)}
						value={col.column}
						name='column'
						onChange={e => onSortingStrategyOptionChange(e, index)}
						className='p-inputtext-sm w-4'
						style={{ blockSize: '2.5rem' }}
					/>
					<Dropdown
						options={Object.keys(ProductTemplateSortingStrategyOptionsOrder)}
						value={col.order}
						name='order'
						onChange={e => onSortingStrategyOptionChange(e, index)}
						className='p-inputtext-sm p-inputgroup-addon'
						style={{ blockSize: '2.5rem' }}
					/>
					<Button
						icon='pi pi-times'
						onClick={() => removeSortingStrategyOption(index)}
						className='p-inputgroup-addon p-button-sm p-button-danger'
						style={{ blockSize: '2.5rem' }}
					/>
				</div>
			</InputLabelWrapper>
		));
	return (
    <Accordion className="p-field p-col-12">
      <AccordionTab header={T("word.settings")}>
        <div className="formgrid grid">
          <InputLabelWrapper labelName={T("chart-config.loading-type")} col={12}>
            <Dropdown
              value={settings.loadingType}
              options={Object.values(TemplateLoadingType)}
              onChange={onFieldChange}
              name="loadingType"
              className="p-inputtext-sm w-4"
            />
          </InputLabelWrapper>
          {settings.loadingType !== TemplateLoadingType.EVERYTHING && (
            <>
              <InputLabelWrapper labelName={T("load-conf.truncate-start-unit")} col={2}>
                <Dropdown
                  value={settings.loadingConfig?.truncateStartUnit}
                  options={Object.keys(ChronoUnitLegacy)}
                  name="truncateStartUnit"
                  onChange={onLoadingConfigChange}
                  className="p-inputtext-sm"
                  style={{ blockSize: "2.5rem" }}
                />
              </InputLabelWrapper>

              <InputLabelWrapper labelName={T("load-conf.period")} col={5}>
                <div className="p-inputgroup">
                  <InputNumber
                    value={settings.loadingConfig?.period ?? 0}
                    name="period"
                    onValueChange={onLoadingConfigChange}
                    className="p-inputtext-sm"
                    style={{ blockSize: "2.5rem" }}
                  />
                  <Dropdown
                    value={settings.loadingConfig?.periodUnit}
                    options={Object.keys(ChronoUnitLegacy)}
                    name="periodUnit"
                    onChange={onLoadingConfigChange}
                    className="p-inputtext-sm p-inputgroup-addon"
                    style={{ blockSize: "2.5rem" }}
                  />
                </div>
              </InputLabelWrapper>

              <InputLabelWrapper labelName={T("load-conf.offset")} col={5}>
                <div className="p-inputgroup">
                  <InputNumber
                    value={settings.loadingConfig?.offset ?? 0}
                    name="offset"
                    onValueChange={onLoadingConfigChange}
                    className="p-inputtext-sm"
                    style={{ blockSize: "2.5rem" }}
                  />
                  <Dropdown
                    value={settings.loadingConfig?.offsetUnit}
                    options={Object.keys(ChronoUnitLegacy)}
                    name="offsetUnit"
                    onChange={onLoadingConfigChange}
                    className="p-inputtext-sm p-inputgroup-addon"
                    style={{ blockSize: "2.5rem" }}
                  />
                </div>
              </InputLabelWrapper>

              <InputLabelWrapper labelName={T("load-conf.start-attribute")}>
                <Dropdown
                  value={settings.startAttribute}
                  options={attributesCopy.map(att => att.referenceName)}
                  name="startAttribute"
                  onChange={onFieldChange}
                  className="p-inputtext-sm"
                />
              </InputLabelWrapper>
              <InputLabelWrapper labelName={T("load-conf.stop-attribute")}>
                <Dropdown
                  value={settings.stopAttribute}
                  options={attributesCopy.map(att => att.referenceName)}
                  name="stopAttribute"
                  onChange={onFieldChange}
                  className="p-inputtext-sm"
                />
              </InputLabelWrapper>
            </>
          )}

          <InputLabelWrapper labelName={T("load-conf.signature-attribute")}>
            <Dropdown
              value={settings.signatureAttribute}
              options={attributesCopy
                .filter(att => att.type === CellType.USER_SIGNATURE)
                .map(att => att.referenceName)}
              name="signatureAttribute"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName={T("load-conf.to-string-formatter")}>
            <InputText
              placeholder={T("load-conf.to-string-formatter")}
              value={settings.toStringFormatter ?? ""}
              name="toStringFormatter"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>

          <InputLabelWrapper labelName={T("load-conf.row-per-page")} col={4}>
            <Dropdown
              value={settings.rowsPerPage}
              options={[5, 10, 25, 50, 100]}
              name="rowsPerPage"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>

          <InputLabelWrapper labelName={T("load-conf.timezone-id")} col={4}>
            <InputText
              value={settings.timezoneId ?? ""}
              name="timezoneId"
              disabled
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>

          <Fieldset
            legend={T("load-conf.sorting-strategy-options")}
            className="w-full mt-4"
            toggleable
          >
            <div className="formgrid grid">
              <InputLabelWrapper labelName={T("load-conf.sorting-strategy")}>
                <Dropdown
                  value={settings.sortingStrategy}
                  options={Object.values(ProductTemplateSortingStrategy)}
                  name="sortingStrategy"
                  onChange={onFieldChange}
                  className="p-inputtext-sm"
                />
              </InputLabelWrapper>

              <InputLabelWrapper
                labelName={T("load-conf.sorting-reversed")}
                className="justify-content-evenly align-items-center"
              >
                <InputSwitch
                  checked={settings.sortingReversed ?? false}
                  name="sortingReversed"
                  onChange={onFieldChange}
                  className="p-inputtext-sm"
                />
              </InputLabelWrapper>
              {mappedSortingStrategyOptions}
            </div>
            <Tooltip id="add-sorting-attribute" />
            <Button
              data-tooltip-id="add-sorting-attribute"
              data-tooltip-content="Add sorting attribute"
              data-tooltip-place="right"
              icon="pi pi-plus"
              className="p-button-outlined p-button-success p-button-rounded mt-4 ml-4"
              onClick={addSortingStrategyOption}
            />
          </Fieldset>

          <InputLabelWrapper labelName={T("load-conf.display-total-row")} col={4}>
            <InputSwitch
              checked={settings.displayTotalRow ?? false}
              name="displayTotalRow"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName={T("component.allow-generate-report")} col={8}>
            <InputSwitch
              checked={settings.allowGenerateReport ?? false}
              name="allowGenerateReport"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName={T("component.allow-row-addition")} col={4}>
            <InputSwitch
              checked={settings.allowRowAddition ?? true}
              name="allowRowAddition"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName={T("component.allow-row-deletion")} col={4}>
            <InputSwitch
              checked={settings.allowRowDeletion ?? true}
              name="allowRowDeletion"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName={T("component.allow-row-edit")} col={4}>
            <InputSwitch
              checked={settings.allowRowEdit ?? true}
              name="allowRowEdit"
              onChange={onFieldChange}
              className="p-inputtext-sm"
            />
          </InputLabelWrapper>
        </div>
      </AccordionTab>
    </Accordion>
  );
};

export default ProductTemplateSettingsConfiguration;
